import { DropitState } from "."
import { IRawSupplierReference } from "../../models"
import combineReducers from "../../utils/combine-reducer"
import { ActionTypes } from "../actions/act_types"
import { createSelector } from "../../utils/memo"

const isLoading = (state = 0, action: any): number => {
  switch (action.type) {
    case ActionTypes.START_FETCHING_PRODUCTS:
      return ++state
    case ActionTypes.FETCH_PRODUCTS_FAIL:
    case ActionTypes.FETCH_PRODUCTS_SUCCESS:
      return --state
    default:
      return state
  }
}

interface IRawRefByDroopyId {
  [droopyId: string]: IRawSupplierReference
}

const byDroopyId = (
  state: IRawRefByDroopyId = {},
  action: any
): IRawRefByDroopyId => {
  if (action.response) {
    if (action.response.entities) {
      return {
        ...state,
        ...action.response.entities.products,
      }
    }
  }

  return state
}

const allDroopyIds = (state: string[] = [], action: any): string[] => {
  switch (action.type) {
    case ActionTypes.FETCH_PRODUCTS_SUCCESS:
      return action.response.result
    case ActionTypes.DELETE_PRODUCTS:
      return state.filter((droopyId) => !action.ids.includes(droopyId))
    default:
      return state
  }
}

const errorMessage = (state = "", action: any): string => {
  switch (action.type) {
    case ActionTypes.START_FETCHING_PRODUCTS:
    case ActionTypes.FETCH_PRODUCTS_SUCCESS:
      return ""
    case ActionTypes.FETCH_PRODUCTS_FAIL:
      return action.errorMessage
    default:
      return state
  }
}

const selectedDroopyIds = (state: string[] = [], action: any): string[] => {
  switch (action.type) {
    case ActionTypes.SELECT_ROW:
      return [...state, action.droopyId]
    case ActionTypes.UNSELECT_ROW:
      return state.filter((x) => x !== action.droopyId)
    case ActionTypes.SELECT_ALL_ROWS:
      return action.ids
    case ActionTypes.UNSELECT_ALL_ROWS:
      return []
    default:
      return state
  }
}

const showDeletedProducts = (state = false, action: any): boolean => {
  switch (action.type) {
    case ActionTypes.SHOW_DELETED_PRODUCTS:
      return !state
    default:
      return state
  }
}

const productsReducer = combineReducers({
  allDroopyIds,
  byDroopyId,
  errorMessage,
  isLoading,
  selectedDroopyIds,
  showDeletedProducts,
})

export default productsReducer

const selectAllDroopyIds = (state: DropitState) => state.products.allDroopyIds
const selectByDroopyId = (state: DropitState) => state.products.byDroopyId

// Memoized: a new array on every call made every connected component re-render on any Redux action.
export const getProductList = createSelector(
  [selectAllDroopyIds, selectByDroopyId],
  (allDroopyIds: string[], byDroopyId: IRawRefByDroopyId) =>
    allDroopyIds.map((droopyId: string) => byDroopyId[droopyId])
)

export const getProductWithDroopyId = (state: DropitState, droopyId: string) =>
  state.products.byDroopyId[droopyId]
export const getProductListIsLoading = (state: DropitState) =>
  state.products.isLoading > 0
export const getProductListErrorMessage = (state: DropitState) =>
  state.products.errorMessage
export const getSelectedDroopyIds = (state: DropitState) =>
  state.products.selectedDroopyIds
export const isThisProductValid = (state: DropitState, id: string) =>
  state.products.byDroopyId[id].isValid
export const getShowDeletedProducts = (state: DropitState) =>
  state.products.showDeletedProducts

export const getSelectedSupplierRef = (state: DropitState) =>
  state.products.selectedDroopyIds.map((droopyId: string) => {
    return getSupplierRefByDroopyId(state, droopyId)
  })

export const getSupplierRefByDroopyId = (
  state: DropitState,
  droopyId: string
) => state.products.byDroopyId[droopyId].supplierRef
